import { AnyDateType, toDate } from '@art-suite/art-core-ts';
import {
  apiDate,
  number,
  optional,
  optionalBoolean,
  optionalNumber,
  optionalString,
  optionalUuid,
  string,
  uuid,
  zenum,
  zodLess,
  ZodLessInfer,
} from '@kaiizen/zod-less';
import { makeOptionalPartialMetadataSchema, optionalApiDate } from '../schemaLib';

// *******************************************************************
// Scan API
// *******************************************************************
export const ScanLifecycleStatusSchema = zenum(
  'created',
  'dispatched',
  'inProgress',
  'pendingReview',
  'inReview',
  'reviewed',
  'approved',
  'rejected',
  'correctionsRequested',
);
export const ScanTypeSchema = zenum('initial', 'final', 'changeRequest', 'progress', 'progressRework', 'progressCompleted', 'audienceDoc');
export type ScanType = ZodLessInfer<typeof ScanTypeSchema>;
export const ActiveScanLifecycleStatusSchema = zenum('created', 'dispatched', 'inProgress', 'pendingReview', 'inReview', 'reviewed', 'correctionsRequested');
export type ScanLifecycleStatus = ZodLessInfer<typeof ScanLifecycleStatusSchema>;
export const activeScanLifecycleStatusOptions: ScanLifecycleStatus[] = ActiveScanLifecycleStatusSchema.options;
export const ScanLifecycleStatusesSchema = zodLess([ScanLifecycleStatusSchema]);
export type ScanLifecycleStatuses = ZodLessInfer<typeof ScanLifecycleStatusesSchema>;

const PhotoStatusRecords = {
  photoCount: optionalNumber.nullable().describe('The total number of photos referenced in the scan data. This could have been called photosInScanDataCount.'),
  syncedPhotoCount: optionalNumber.nullable().describe(
    'Number of photos referenced in the scan data that have been synced to the database. This is the set-intersection of photo referenced in the scan-data and photos in the database based on their IDs.',
  ),
  photosInDbCount: optionalNumber.nullable().describe('Number of photos that are currently in the database for this scan.'),
};
export const ApiScanPhotoStatusSchema = zodLess(PhotoStatusRecords, { ref: 'scanPhotoStatus' });
export type ApiScanPhotoStatus = ZodLessInfer<typeof ApiScanPhotoStatusSchema>;

export const ApiScanCustomerMetadataFields = {
  gateCode: optionalString.nullable(),
  doorCode: optionalString.nullable(),
  dispatchNotes: optionalString.nullable(),
  dueDate: optionalApiDate.nullable(),
  audienceDocResidentResponsibleOnly: optionalBoolean.nullable().describe(
    'When true, audience-doc rendering should only include line items tagged tenantResponsibility.',
  ),
  audienceDocShowAmounts: optionalBoolean.nullable().describe(
    'When false, audience-doc rendering hides rate, amount, and total values.',
  ),
  audienceDocIncludePhotos: optionalBoolean.nullable().describe(
    'When false, audience-doc rendering hides task photos.',
  ),
  audienceDocPhotosOnly: optionalBoolean.nullable().describe(
    'When true, audience-doc rendering shows task photos only rather than scope content.',
  ),
  audienceDocShowOnlyChangeOrderChanges: optionalBoolean.nullable().describe(
    'When true, audience-doc rendering includes only line items that belong to the change-order diff for this copy.',
  ),
  // aggregations & latest ScanRootOptimized-derived fields
  ...PhotoStatusRecords,
  notesCount: optionalNumber.nullable(),
  generalContractorOrgContractorId: optionalString.nullable(),
  scopedTaskCount: optionalNumber.nullable().describe(
    "The total number of tasks that are scoped to the project (includes both 'documentation' ($0) tasks and normal, cost != $0 tasks).",
  ),
  scopedTasksWithNonZeroCostCount: optionalNumber.nullable().describe(
    "The number of tasks that are scoped to the project and have a non-zero cost ('documentation', cost == $0 tasks are excluded).",
  ),
};
export const ApiScanCustomerMetadataSchema = zodLess(ApiScanCustomerMetadataFields, { ref: 'scanCustomerMetadata' });
export type ApiScanCustomerMetadata = ZodLessInfer<typeof ApiScanCustomerMetadataSchema>;

export const ApiScanSchema = zodLess({
  id: uuid,
  createdAt: apiDate,
  updatedAt: apiDate,
  approvedAt: optionalApiDate,
  archivedAt: optionalApiDate,
  orgId: uuid.describe('The organization this scan is associated with. This affects access-control of the scan.'),
  name: string,
  scanType: optional(ScanTypeSchema),
  scanLifecycleStatus: ScanLifecycleStatusSchema, // automatically set when creating, but can be updated directly
  customerMetadata: makeOptionalPartialMetadataSchema(ApiScanCustomerMetadataFields),
  propertyProjectId: uuid.describe('The property project this scan is associated with.'),
  scanTemplateId: uuid.describe(
    'The scan template ID. creatable, but not updatable. Used in conjunction with scanTemplateAt to uniquely determine the scan-template version.',
  ),
  scanTemplateAt: apiDate.describe('Timestamp of the version this was based on. automatically set when creating'),
  parentScanId: optional(uuid).describe(
    'If this scan is a fork of another scan. Used in conjunction with parentScanAt to uniquely determine the source scan version. creatable, but not updatable (example: used for change orders)',
  ),
  parentScanAt: optionalApiDate.describe(
    'Timestamp of the snapshot this scan was branched from. Used in conjunction with parentScanId to uniquely determine the source scan version. automatically set when creating a fork',
  ),
  heroScanPhotoId: optionalUuid.describe('The scan photo ID to use as the hero image for the scan. creatable, but not updatable.'),
  totalMaterialCost: optional(number),
  totalLaborCost: optional(number),
}, { ref: 'scan', description: 'A scan represents a specific instance of a property inspection using a scan template.' });

export const ApiCreateScanSchema = ApiScanSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    archivedAt: true,
    scanLifecycleStatus: true,
    orgId: true,
    scanTemplateAt: true,
    propertyProjectId: true,
    scanTemplateId: true,
    heroScanPhotoId: true,
  })
  .extend({
    scanTemplateAt: apiDate.optional(),
    propertyProjectId: uuid.optional(),
    scanTemplateId: uuid.optional(),
  })
  // now, check if EITHER parentScanId is set OR both propertyProjectId and scanTemplateId are set
  .refine(data => {
    const hasParentScan = !!data.parentScanId;
    const hasPropertyProject = !!data.propertyProjectId;
    const hasScanTemplate = !!data.scanTemplateId;

    return (hasParentScan && !hasPropertyProject && !hasScanTemplate) // create child of scan
      || (hasPropertyProject && hasScanTemplate && !hasParentScan); // create new scan from template
  }, { message: 'Either parentScanId or both propertyProjectId and scanTemplateId must be set' })
  .refine(
    data => !!data.parentScanId || !data.scanType || data.scanType === 'initial',
    { message: 'Only initial scans can be created without parentScanId' },
  );

export const ApiUpdateScanSchema = ApiScanSchema.omit({
  id: true,
  scanTemplateId: true,
  scanTemplateAt: true,
  createdAt: true,
  updatedAt: true,
  parentScanAt: true,
  parentScanId: true,
  // scanType is server-derived (Scan.ts's getScanType, recomputed from scan
  // content on every lifecycle/data change) and never client-settable via
  // update - only creation (ApiCreateScanSchema) sets it directly. The one
  // internal exception, cancelChangeOrder resolving a change-request scan
  // back to its actual post-revert type, goes through updateScan's
  // options.resolvedScanType, not this schema.
  scanType: true,
}).extend({ archivedAt: string.nullable().optional() }).partial();
export type ApiScan = ZodLessInfer<typeof ApiScanSchema>;
export type ApiCreateScan = ZodLessInfer<typeof ApiCreateScanSchema>;
export type ApiUpdateScan = ZodLessInfer<typeof ApiUpdateScanSchema>;

const getRealtimeFromSuffix = (from?: Date | AnyDateType): string => {
  if (!from) return '';
  return `?from=${toDate(from).toISOString()}`;
};

export const getScanQueryKey = (scanId: string | undefined) => scanId ? `scan/${scanId}` : '';
export const getScansQueryPrefix = () => `scans`;
export const getActiveScansQueryKey = (options?: { archived?: boolean }) => `scans/active/${options?.archived ? 'archived' : 'notArchived'}`;
export const getScansByPropertyProjectIdQueryKeyPrefix = (propertyProjectId: string | undefined) => `scans/byPropertyProjectId/${propertyProjectId ?? ''}`;
export const getScansByPropertyProjectIdQueryKey = (
  propertyProjectId: string | undefined,
  options?: { archived?: boolean; includeAudienceDocs?: boolean },
) =>
  `${getScansByPropertyProjectIdQueryKeyPrefix(propertyProjectId)}/${options?.archived ? 'archived' : 'active'}/${
    options?.includeAudienceDocs ? 'withAudienceDocs' : 'workflowOnly'
  }`;
export const getScansForUserQueryKey = (userId: string | undefined) => `scans/forUser/${userId}`;

export const getScanDataQueryKey = (
  scanId: string | undefined,
  from?: Date | AnyDateType,
) => (scanId ? `scanDataV2/${scanId}${getRealtimeFromSuffix(from)}` : '');

export const getScanPartHistoryQueryKey = (
  scanId: string | undefined,
  scanPartId: string | undefined,
  { from, to, limit }: { from?: Date; to?: Date; limit?: number } = {},
) => `scanPartHistory/byScanId/${scanId ?? ''}/${scanPartId ?? ''}?from=${from?.toISOString() ?? ''}&to=${to?.toISOString() ?? ''}&limit=${limit ?? ''}`;

export const getScanDataMetadataQueryKey = (
  scanId: string | undefined,
  from?: Date | AnyDateType,
) => (scanId ? `scanDataDeltaMetadata/${scanId}${getRealtimeFromSuffix(from)}` : '');
